class MaProduct extends HTMLElement {
  connectedCallback() {
    const raw = this.querySelector('[data-variants]');
    this.variants = raw ? JSON.parse(raw.textContent) : [];
    this.baseUrl = this.dataset.url || window.location.pathname;
    this.form = this.querySelector('[data-buy-form]');
    this.input = this.querySelector('[data-variant-input]');
    this.priceNode = this.querySelector('[data-price]');
    this.addButton = this.querySelector('[data-add]');
    this.skuNode = this.querySelector('[data-sku]');
    this.stockNode = this.querySelector('[data-stock]');
    this.stage = this.querySelector('[data-stage]');

    this.onClick = this.onClick.bind(this);
    this.onKey = this.onKey.bind(this);
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKey);

    const active = this.querySelector('[data-thumb][aria-current="true"]');
    if (active) this.show(active.dataset.thumb, false);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKey);
  }

  onKey(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (!event.target.closest('[data-thumb]')) return;
    const thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
    const at = thumbs.indexOf(event.target.closest('[data-thumb]'));
    const next = thumbs[event.key === 'ArrowRight' ? at + 1 : at - 1];
    if (!next) return;
    event.preventDefault();
    next.focus();
    this.show(next.dataset.thumb, true);
  }

  onClick(event) {
    const thumb = event.target.closest('[data-thumb]');
    if (thumb) {
      event.preventDefault();
      this.show(thumb.dataset.thumb, true);
      return;
    }
    const step = event.target.closest('[data-step]');
    if (step) {
      event.preventDefault();
      this.step(parseInt(step.dataset.step, 10));
      return;
    }
    const swatch = event.target.closest('[data-value]');
    if (swatch) {
      event.preventDefault();
      this.pick(swatch);
    }
  }

  step(direction) {
    const field = this.querySelector('[data-qty]');
    if (!field) return;
    const next = Math.max(1, (parseInt(field.value, 10) || 1) + direction);
    field.value = next;
    field.dispatchEvent(new Event('change', { bubbles: true }));
  }

  pick(button) {
    const group = button.closest('[data-option]');
    if (!group) return;
    group.querySelectorAll('[data-value]').forEach((node) => {
      node.setAttribute('aria-pressed', node === button ? 'true' : 'false');
    });
    this.sync();
  }

  selected() {
    return Array.from(this.querySelectorAll('[data-option]')).map((group) => {
      const on = group.querySelector('[data-value][aria-pressed="true"]');
      return on ? on.dataset.value : null;
    });
  }

  match(values) {
    return this.variants.find((variant) =>
      variant.options.every((option, index) => option === values[index])
    );
  }

  sync() {
    const values = this.selected();
    const variant = this.match(values) || this.variants[0];
    if (!variant) return;

    if (this.input) this.input.value = variant.id;

    if (this.priceNode) {
      const parts = [];
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        parts.push('<s>' + this.money(variant.compare_at_price) + '</s>');
      }
      parts.push('<span>' + this.money(variant.price) + '</span>');
      this.priceNode.innerHTML = parts.join(' ');
    }

    if (this.skuNode) {
      this.skuNode.textContent = variant.sku || '';
      this.skuNode.hidden = !variant.sku;
    }

    if (this.stockNode) {
      this.stockNode.textContent = variant.available
        ? this.stockNode.dataset.inStock
        : this.stockNode.dataset.outOfStock;
      this.stockNode.classList.toggle('is-out', !variant.available);
    }

    if (this.addButton) {
      const text = variant.available
        ? this.addButton.dataset.labelAdd
        : this.addButton.dataset.labelSold;
      const label = this.addButton.querySelector('[data-add-label]');
      const shadow = this.addButton.querySelector('[data-add-shadow]');
      this.addButton.disabled = !variant.available;
      if (label) label.textContent = text;
      if (shadow) shadow.setAttribute('data-label', text);
    }

    this.querySelectorAll('[data-option]').forEach((group, index) => {
      group.querySelectorAll('[data-value]').forEach((node) => {
        const probe = values.slice();
        probe[index] = node.dataset.value;
        const found = this.match(probe);
        node.classList.toggle('is-gone', !found || !found.available);
      });
    });

    if (variant.featured_media) this.show(String(variant.featured_media.id), true);

    if (window.history && window.history.replaceState) {
      window.history.replaceState({}, '', this.baseUrl + '?variant=' + variant.id);
    }
  }

  show(id, scroll) {
    if (!this.stage || !id) return;
    let found = false;
    this.stage.querySelectorAll('[data-media]').forEach((slide) => {
      const on = slide.dataset.media === id;
      if (on) found = true;
      slide.classList.toggle('is-on', on);
      slide.setAttribute('aria-hidden', on ? 'false' : 'true');
    });
    this.querySelectorAll('[data-thumb]').forEach((thumb) => {
      thumb.setAttribute('aria-current', thumb.dataset.thumb === id ? 'true' : 'false');
    });
    if (!found || !scroll) return;
    const rail = this.querySelector('[data-rail]');
    const current = this.querySelector('[data-thumb][aria-current="true"]');
    if (rail && current && rail.scrollWidth > rail.clientWidth) {
      rail.scrollTo({ left: current.offsetLeft - 24, behavior: 'smooth' });
    }
  }

  money(cents) {
    const format = window.MA_MONEY || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const parts = amount.split('.');
    const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace('{{amount}}', whole + '.' + parts[1])
      .replace('{{amount_no_decimals}}', whole)
      .replace('{{amount_with_comma_separator}}', whole.replace(/,/g, '.') + ',' + parts[1])
      .replace('{{amount_no_decimals_with_comma_separator}}', whole.replace(/,/g, '.'));
  }
}

if (!customElements.get('ma-product')) customElements.define('ma-product', MaProduct);
